import equals from 'fast-deep-equal';
import clone from 'clone'

import { smooth } from 'util/table/Smooth';

/**
 * @typedef {Object} Point - reference to an element in a matrix
 * @property {number} i - the row index of the element
 * @property {number} j - the column index of the element
 */

/**
 * @typedef {Object} Selection - two points describing a selected area
 * @property {Point} topLeft - the top left corner
 * @property {Point} bottomRight - the bottom right corner
 */

/**
 * @typedef {Object} Shape - an object describing the dimensions of a matrix
 * @property {number} m - the column length of the matrix
 * @property {number} n - the row length of the matrix
 */

export class MatrixError extends Error {
    constructor(message) {
        super(message);
        this.name = "MatrixError";
    }
}

/**
 * Class representing a matrix
 */
export default class Matrix {
    /**
     * @type {number[][]}
     * @readonly
     */
    values;
    /**
     * @type {number}
     * @readonly
     * */
    precision;
    /**
     * @type {Object}
     * @readonly
     * */
    shape;

    //todo delete parsefloat stuff once carl's deployed string/number fix
    /**
     * Instances a matrix
     * @param {object} obj - object representing a matrix
     * @param {(number|string)[][]} obj.values - 2D array of numbers
     * @param {number|string} [obj.precision] - the number of decimal places the matrix formats to
     */
    constructor ({values, precision}) {
        // A precision of 0 is one (the values show as whole numbers), so only a missing precision is skipped. `if (precision)` skipped the 0 as well.
        if (precision !== undefined && precision !== null && precision !== "") {
            if (isNaN(parseFloat(precision))) throw new MatrixError("precision is not a number");
            this.precision = precision >=0 && precision <=100 ? parseFloat(precision) : 0; //fix for ols bug
        }
        this.values = this._validateValues(values);
        this.shape = { m: this.values.length, n: this.values[0].length };

        Object.seal(this); //stop additional fields from being added to new obj
        Object.freeze(this);
    }

    // public methods

    /**
     * Checks that every matrix element satisfies the conditions
     * @param {function(number):boolean} callBack - function representing conditions
     * @returns {boolean}
     */
    every(callBack) {
        return this.values.every(row => row.every(callBack))
    }

    /**
     * Reverse elements in i direction
     * @return {Matrix} result - the resulting matrix
     */
    reverseI() {
        return new Matrix({
            ...this,
            values: [...this.values].reverse()
        });
    }

    /**
     * Reverse elements in j direction
     * @return {Matrix} result - the resulting matrix
     */
    reverseJ() {
        return new Matrix({
            ...this,
            values: this.values.map(row => [...row].reverse())
        });
    }

    /**
     * Smooths the matrix values
     * @return {Matrix} result - the resulting matrix
     */
    smooth() {
        return new Matrix({
            ...this,
            values: smooth(this.values)
        });
    };

    /**
     * Multiply values of matrix by corresponding values from given matrix
     * @throws will throw error if matrix dimensions don't match
     * @param {Matrix} matrix - given matrix to multiply by
     * @return {Matrix} result - the resulting matrix
     */
    elementwiseMultiply(matrix) {
        if (!equals(matrix.shape, this.shape)) throw new MatrixError("Matrix dimensions don't match");

        const values = this.values.map((row, i) => {
            return row.map((cell, j) => {
                return matrix.values[i][j] * cell;
            })
        });

        return new Matrix({...this, values});
    }

    /**
     * Applies a transform function to the matrix
     * @throws will throw an error if given formula doesn't map to a number for every value in the matrix
     * @param {function} [formula] - function to apply
     * @return {Matrix} result - the resulting matrix
     */
    transform(formula) {
        const values = this.values.map((row) => {
            return row.map((x) => {
                let f = formula(x);

                // verify that f is a number
                if (typeof f !== 'number' || !Number.isFinite(f)) {
                    throw new MatrixError('f(x) doesn\'t map to a number for every x');
                }

                return f;
            })
        });

        return new Matrix({...this, values});
    };

    /**
     * Use a selection to extract a sub-matrix from the matrix
     * @throws will throw an error if the start or end points are invalid for this matrix
     * @param {Point} start
     * @param {Point} end
     * @return {Matrix} result - the resulting matrix that corresponds to the selection
     */
    extract(start, end) {
        const {topLeft, bottomRight} = this.selection(start, end);
        const values = this.values.slice(topLeft.i, bottomRight.i + 1).map(a => a.slice(topLeft.j, bottomRight.j + 1));

        return new Matrix({...this, values});
    };

    /**
     * Inserts a sub-matrix into the matrix starting from a given point and finishing at bottom left of the matrix (ignores overflow)
     * @throws will throw an error if the start point is invalid for this matrix
     * @param {Matrix} subMatrix - matrix to insert
     * @param {Point} start - starting point
     * @return {Matrix} result - the resulting matrix
     */
    insert(subMatrix, start) {
        let values = clone(this.values); // to maintain integrity since splice edits the original
        const { topLeft } = this.selection(start, {i: this.shape.m-1, j: this.shape.n-1}); // make a selection from start to bottom left value of matrix

        subMatrix.values.forEach((row, i) => {
            //stop vertical overflow
            if (topLeft.i+i < this.shape.m){
                row = row.filter((_, j) => { return topLeft.j + j < this.shape.n }); //truncate row to stop horizontal overflow
                values[topLeft.i+i].splice(start.j, row.length, ...row)
            }
        });

        return new Matrix({...this, values});
    };

    /**
     * Apply max and min limit to all matrix values
     * @return {Matrix} result - The resulting limited matrix
     */
    limit(min, max) {
        let values = this.values.map((row) => {
            return row.map((x) => {
                if (max !== null && x > max) return max;
                if (min !== null && x < min) return min;
                return x;
            })
        });

        return new Matrix({...this, values});
    };

    /**
     * Create a valid selection (described by top left and bottom right point) from any pair of points describing an area
     * @throws will throw an error if the start or end points are invalid for this matrix
     * @param {Point} start - first point selected
     * @param {Point} end - second point selected
     * @returns {Selection} selection - selection starting from top left and ending bottom right of area
     */
    selection(start, end) {
        if (!start.i?.isBetween(0, this.shape.m-1) || !start.j?.isBetween(0, this.shape.n-1))
            throw new MatrixError("Invalid start point");
        if (!end.i?.isBetween(0, this.shape.m-1) || !end.j?.isBetween(0, this.shape.n-1))
            throw new MatrixError("Invalid end point");

        // Determine top left and bottom right corners regardless of corner pair selected ie. drag direction
        return {
            topLeft: {
                i: start.i > end.i ? end.i : start.i,
                j: start.j > end.j ? end.j : start.j
            },
            bottomRight: {
                i: end.i > start.i ? end.i : start.i,
                j: end.j > start.j ? end.j : start.j
            },
        };
    };

    /**
     * Returns a copy of the array values where elements are rounded, optionally in stringified form
     * @param stringify
     * @returns {(number|string)[][]}
     */
    formatData(stringify=true) {
        return this.values.map(row => row.map(x => {
            x = this.precision !== undefined ? x.toFixed(this.precision) : x.toString();
            return stringify ? x : parseFloat(x);
        }));
    };

    toExcelString() {
        return this.values.map(row => row.join("\t")).join("\n");
    }

    // private methods

    /**
     * Checks that values is a valid matrix
     * @throws will throw an error if the given values is not a valid matrix
     * @param {number[][] || number[]} values - 1D or 2D array of numbers
     * @return {number[][]} result - the validated 2D array
     * @private
     */
    _validateValues(values) {
        if (!Array.isArray(values)) throw new MatrixError("values is not an array");

        //transform 1D arrays into 2D by wrapping in extra array
        if (!Array.isArray(values[0])) values = [values];

        const valuesHasConsistentArrays = values.every(row => Array.isArray(row) && row.length === values[0].length);
        if (!valuesHasConsistentArrays) throw new MatrixError("rows in values are of inconsistent length or not every row is an array");

        const valuesOnlyContainsNumbers = values.every(row => row.every(elem => typeof elem === "number" && !isNaN(elem)));
        if (!valuesOnlyContainsNumbers) throw new MatrixError("not every element of values is a number");

        return values
    }

    // static methods

    /**
     * Factory function that generates a new matrix filled with zeros
     * @param {Shape} shape - the dimensions of the new matrix
     * @param {number} [precision] - the number of decimal places for element values
     * @returns {Matrix}
     */
    static zeros(shape, precision=0) {
        const values = [...Array(shape.m)].map(x => Array(shape.n).fill(0));
        return new Matrix({values, precision});
    }

    /**
     * Factory function that generates a new 2D matrix with a corner-to-corner linear gradient
     * @throws will throw an error if shape is not at least 2x2, xs/ys is undefined, or xs/ys length is inconsistent with shape
     * @param {Shape} shape - the dimensions of the new matrix
     * @param {number} start - the smallest value of the gradient (top-left)
     * @param {number} stop - the largest value of the gradient (bottom-right)
     * @param {number} precision - the precision to pass to matrix constructor
     * @param {number[]} [xs] - the x-axis values
     * @param {number[]} [ys] - the y-axis values
     * @returns {Matrix}
     */
    static cornerGradient(shape, start, stop, precision, xs, ys) {
        if (shape.m <= 1 || shape.n <= 1) throw new MatrixError("Matrix must be at least 2D");
        if (!(xs && ys)) throw new MatrixError("xs and ys must be defined");

        if (ys.length !== shape.m) throw new MatrixError("ys length is inconsistent with matrix shape");
        if (xs.length !== shape.n) throw new MatrixError("xs length is inconsistent with matrix shape");

        const matrix = this.zeros(shape, precision);

        const xgrad = (stop-start)/(xs[xs.length-1]-xs[0]); // m = (y2-y1) / (x2-x1)
        const ygrad = (stop-start)/(ys[ys.length-1]-ys[0]);
        const y = x => xgrad*(x-xs[0]) + start; // y = m(x-x1) x y1
        const x = y => ygrad*(y-ys[0]) + start;

        const values = matrix.values.map((row, i) => {
            return row.map((cell, j) => {
                return (x(ys[i]) + y(xs[j]))/2;
            })
        });

        return new Matrix({values, precision});
    }

    /**
     * Factory function that generates a new 2D matrix with an edge-to-edge linear gradient
     * @throws will throw an error if shape is not at least 2x2, either xs/ys is undefined, or xs/ys length is inconsistent with shape
     * @param {Shape} shape - the dimensions of the new matrix
     * @param {number} start - the smallest value of the gradient (left)
     * @param {number} stop - the largest value of the gradient (right)
     * @param {number} precision - the precision to pass to matrix constructor
     * @param {number[]} [ys] - the x-axis values
     * @param {number[]} [xs] - the y-axis values
     * @returns {Matrix}
     */
    static edgeGradient(shape, start, stop, precision, xs, ys) {
        if (shape.m < 1 || shape.n < 1) throw new MatrixError("Matrix must be at least 2D");
        if (!(ys || xs)) throw new MatrixError("Either xs or ys must be defined");

        if (xs && xs.length !== shape.n) throw new MatrixError("ys length is inconsistent with matrix shape");
        if (ys && ys.length !== shape.m) throw new MatrixError("xs length is inconsistent with matrix shape");

        const matrix = this.zeros(shape, precision);

        const grad = ys ? (stop-start)/(ys[ys.length-1]-ys[0]) : (stop-start)/(xs[xs.length-1]-xs[0]); // m = (y2-y1) / (x2-x1)
        const y = ys ? x => grad*(x-ys[0]) + start : x => grad*(x-xs[0]) + start; // y = m(x-x1) x y1

        const values = matrix.values.map((row, i) => {
            return row.map((cell, j) => {
                 return y(ys ? ys[i] : xs[j]);
            })
        });

        return new Matrix({values, precision});
    }

    static fromExcelString(excelString, precision) {
        const values = excelString.split("\n").map(row => row.split("\t").map(cell => parseFloat(cell)))
        return new Matrix({values, precision});
    }
}

// add isBetween() function to number prototype eg: (1).isBetween(0,2) === true
// eslint-disable-next-line no-extend-native
Number.prototype.isBetween = function (min, max) {
    return this >= min && this <= max;
};